import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { UserManager } from 'oidc-client-ts';
import { oidcConfig, cognitoLogoutUrl } from './authConfig';

const userManager = new UserManager(oidcConfig);

// Non-hook accessors so apiFetch.js (outside React render) can read the
// current token and trigger re-login without needing the AuthContext.
export async function getAccessToken() {
  const user = await userManager.getUser();
  if (!user || user.expired) {
    return null;
  }
  return user.access_token;
}

// Called by apiFetch.js when a backend actually rejects a request as
// unauthorized (401) — the one reliable signal that the session is truly
// over, as opposed to a transient renewal hiccup (see onSilentRenewError
// below, which deliberately does NOT do this).
export function triggerReauth() {
  return userManager.signinRedirect({ state: { returnTo: window.location.pathname + window.location.search } });
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const onUserLoaded = (u) => setUser(u);
    const onUserUnloaded = () => setUser(null);
    // Deliberately NOT clearing the user / forcing a re-login here. A
    // single failed renewal attempt (e.g. a transient network blip) used
    // to immediately force-navigate the whole page away via RequireAuth,
    // losing any unsaved work the user had in progress. The current
    // (possibly stale) token just keeps being used until a real API call
    // actually gets rejected with 401 — that's what triggerReauth() in
    // apiFetch.js handles — which is a reliable signal, unlike "one
    // renewal attempt didn't work."
    const onSilentRenewError = (err) => {
      console.warn('Token renewal failed (will retry / rely on 401 handling):', err);
    };

    userManager.events.addUserLoaded(onUserLoaded);
    userManager.events.addUserUnloaded(onUserUnloaded);
    userManager.events.addSilentRenewError(onSilentRenewError);

    // Deliberately NOT using signinSilent() here. It checks for an
    // existing session via a hidden iframe to Cognito's domain — but
    // Cognito's session cookie is a third-party cookie in that iframe
    // context, which many browsers (Safari by default, Chrome
    // increasingly) block outright. When that happens the iframe never
    // gets a usable response, and oidc-client-ts just sits there until
    // its internal timeout (~10s) before giving up — on every page load.
    // A real top-level redirect (RequireAuth's signinRedirect, triggered
    // below once isLoading/isAuthenticated resolve) isn't subject to
    // that restriction at all: Cognito's cookie is sent normally for a
    // top-level navigation, so it auto-bounces back near-instantly if a
    // session exists, or shows the login form if not — either way, fast.
    userManager.getUser().then((u) => {
      setUser(u && !u.expired ? u : null);
      setIsLoading(false);
    });

    return () => {
      userManager.events.removeUserLoaded(onUserLoaded);
      userManager.events.removeUserUnloaded(onUserUnloaded);
      userManager.events.removeSilentRenewError(onSilentRenewError);
    };
  }, []);

  const signinRedirect = useCallback((args) => userManager.signinRedirect(args), []);
  const signinRedirectCallback = useCallback(() => userManager.signinRedirectCallback(), []);

  // Cognito's /logout endpoint doesn't follow the standard OIDC
  // end_session_endpoint contract (id_token_hint + post_logout_redirect_uri)
  // that userManager.signoutRedirect() would send — it expects client_id +
  // logout_uri instead. Build that URL manually (cognitoLogoutUrl) and
  // navigate there directly.
  //
  // Deliberately NOT calling userManager.removeUser() first: doing so sets
  // isAuthenticated=false a moment before this navigation actually leaves
  // the page, which makes RequireAuth's effect fire signinRedirect() — and
  // since Cognito's session cookie is still valid at that instant (we
  // haven't reached /logout yet), that redirect silently re-logs the user
  // in and wins the race against this navigation. A full page reload
  // (which logout triggers regardless) wipes the in-memory state anyway,
  // so there's nothing to clean up locally first.
  const signoutRedirect = useCallback(() => {
    window.location.href = cognitoLogoutUrl();
  }, []);

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    signinRedirect,
    signinRedirectCallback,
    signoutRedirect,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
